import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, ArrowLeft, CheckCircle2, Upload, FileText } from "lucide-react";
import { MaskedLines } from "../components/Reveal";
import FloatingShapes from "../components/FloatingShapes";
import { api, US_STATES, OFFICES, SMS_CONSENT_TEXT_YES, SMS_CONSENT_TEXT_NO } from "../lib/site";

const STEPS = ["Identity", "Location", "Documents", "Consent", "Review"];

const empty = {
  first_name: "", last_name: "", email: "", mobile_phone: "",
  country: "United States", address: "", city: "", state: "CA", zipcode: "",
  office: OFFICES[0].label, ssn: "", text_consent: null,
};

export default function Apply() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [resume, setResume] = useState(null);
  const [hp, setHp] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (step === 0) {
      if (!form.first_name || !form.last_name || !form.email || !form.mobile_phone) return "Please complete all fields.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Enter a valid email.";
    }
    if (step === 1) {
      if (!form.country || !form.address || !form.city || !form.state || !form.zipcode || !form.office) return "Please complete all fields.";
    }
    if (step === 2) {
      if (!form.ssn) return "SSN is required.";
    }
    if (step === 3) {
      if (form.text_consent === null) return "Please select an option.";
    }
    return null;
  };

  const next = () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) { toast.error("Only PDF and Word documents are allowed."); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("File is too large. Maximum size is 5 MB."); return; }
    setResume(f);
  };

  const submit = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, k === "text_consent" ? String(v) : v));
      fd.append("company_url", hp);
      if (resume) fd.append("resume", resume);
      await api.post("/applications", fd);
      setDone(true);
      toast.success("Application submitted!");
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="apply-page" className="pt-[72px] min-h-screen flex items-center">
        <div className="mx-auto max-w-[1500px] w-full px-6 md:px-10 py-32">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <CheckCircle2 size={48} className="text-cobalt mb-8" />
            <h1 className="font-display uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl text-cobalt">You're in the pool.</h1>
            <p className="text-muted-foreground text-lg mt-6 max-w-xl">Thank you, {form.first_name}. Your application is with our staffing team. We'll reach out as soon as an opportunity matches your profile.</p>
          </motion.div>
        </div>
      </div>
    );
  }

  const pct = ((step + 1) / STEPS.length) * 100;

  return (
    <div data-testid="apply-page" className="relative pt-[72px] overflow-hidden">
      <FloatingShapes />
      <input type="text" name="company_url" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
      <section className="relative z-10 mx-auto max-w-[1100px] px-6 md:px-10 pt-16 md:pt-24">
        <p className="overline text-flame mb-6">Job Seekers · Application</p>
        <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-[11vw] lg:text-[6vw] mb-12">
          <MaskedLines lines={["Apply to work", <span key="2" className="text-cobalt">with us.</span>]} delay={0.2} />
        </h1>

        {/* progress */}
        <div className="mb-14">
          <div className="flex justify-between mb-3">
            {STEPS.map((s, i) => (
              <span key={s} className={`overline ${i <= step ? "text-cobalt" : "text-muted-foreground"}`}>
                <span className="hidden sm:inline">{String(i + 1).padStart(2, "0")} </span>{s}
              </span>
            ))}
          </div>
          <div className="h-[2px] bg-border">
            <motion.div className="h-full bg-flame" animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} />
          </div>
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-[1100px] px-6 md:px-10 pb-28">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.35 }}
          >
            {step === 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <F label="First Name" t="apply-first-name" v={form.first_name} on={set("first_name")} req />
                <F label="Last Name" t="apply-last-name" v={form.last_name} on={set("last_name")} req />
                <F label="Email" t="apply-email" type="email" v={form.email} on={set("email")} req />
                <F label="Mobile Phone Number" t="apply-phone" v={form.mobile_phone} on={set("mobile_phone")} req />
              </div>
            )}

            {step === 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <F label="Country" t="apply-country" v={form.country} on={set("country")} req />
                <F label="City" t="apply-city" v={form.city} on={set("city")} req />
                <div className="md:col-span-2"><F label="Address" t="apply-address" v={form.address} on={set("address")} req /></div>
                <div>
                  <label className="overline text-muted-foreground">State <span className="text-flame">*</span></label>
                  <select data-testid="apply-state" value={form.state} onChange={set("state")} className="field-line mt-2 cursor-pointer">
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <F label="Zipcode" t="apply-zipcode" v={form.zipcode} on={set("zipcode")} req />
                <div className="md:col-span-2">
                  <label className="overline text-muted-foreground">Office <span className="text-flame">*</span></label>
                  <select data-testid="apply-office" value={form.office} onChange={set("office")} className="field-line mt-2 cursor-pointer">
                    {OFFICES.map((o) => <option key={o.id} value={o.label}>{o.label}{o.tag === "Corporate HQ" ? " (Corporate)" : ""}</option>)}
                  </select>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-10">
                <F label="Social Security Number (SSN)" t="apply-ssn" v={form.ssn} on={set("ssn")} req placeholder="XXX-XX-XXXX" />
                <div>
                  <label className="overline text-muted-foreground">Resume — PDF or Word <span className="text-muted-foreground">(optional)</span></label>
                  <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" onChange={onFile} data-testid="apply-resume-input" className="hidden" />
                  <button
                    type="button"
                    data-testid="apply-resume-btn"
                    onClick={() => fileRef.current?.click()}
                    className="mt-3 w-full flex items-center gap-4 border border-dashed border-foreground/30 px-6 py-8 hover:border-cobalt hover:bg-secondary transition-colors duration-300"
                  >
                    {resume ? <FileText size={22} className="text-cobalt" /> : <Upload size={22} className="text-muted-foreground" />}
                    <span className="text-left">
                      <span className="block font-display uppercase tracking-tight text-lg">{resume ? resume.name : "Click to upload your resume"}</span>
                      <span className="overline text-muted-foreground">{resume ? "Tap to replace" : "PDF, DOC or DOCX · max 5 MB · optional"}</span>
                    </span>
                  </button>
                </div>
                <p className="font-mono text-xs text-muted-foreground">Your SSN is transmitted securely and stored only for employment verification.</p>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <p className="overline text-cobalt mb-3">Text Consent</p>
                <ConsentOption
                  testid="consent-yes"
                  selected={form.text_consent === true}
                  onClick={() => setForm((f) => ({ ...f, text_consent: true }))}
                  label="Yes, I authorize"
                  text={SMS_CONSENT_TEXT_YES}
                />
                <ConsentOption
                  testid="consent-no"
                  selected={form.text_consent === false}
                  onClick={() => setForm((f) => ({ ...f, text_consent: false }))}
                  label="No, I do not authorize"
                  text={SMS_CONSENT_TEXT_NO}
                />
              </div>
            )}

            {step === 4 && (
              <div>
                <p className="overline text-cobalt mb-6">Review your application</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5 border-t border-border pt-8">
                  {[
                    ["Name", `${form.first_name} ${form.last_name}`],
                    ["Email", form.email],
                    ["Mobile", form.mobile_phone],
                    ["Country", form.country],
                    ["Address", `${form.address}, ${form.city}, ${form.state} ${form.zipcode}`],
                    ["Office", form.office],
                    ["SSN", form.ssn ? "•••-••-" + form.ssn.slice(-4) : ""],
                    ["Resume", resume?.name || "—"],
                    ["Text Consent", form.text_consent ? "Authorized" : "Not authorized"],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-border pb-3">
                      <span className="overline text-muted-foreground">{k}</span>
                      <span className="text-sm text-right max-w-[60%] truncate">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-14">
          <button
            onClick={back}
            data-testid="apply-back"
            disabled={step === 0}
            className="inline-flex items-center gap-2 overline disabled:opacity-30 hover:text-flame transition-colors"
          >
            <ArrowLeft size={14} /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} data-testid="apply-continue" className="inline-flex items-center gap-2 bg-cobalt text-white px-8 py-4 rounded-full overline hover:bg-flame transition-colors duration-300">
              Continue <ArrowUpRight size={14} />
            </button>
          ) : (
            <button onClick={submit} disabled={loading} data-testid="apply-submit" className="inline-flex items-center gap-2 bg-flame text-white px-8 py-4 rounded-full overline hover:bg-cobalt transition-colors duration-300 disabled:opacity-50">
              {loading ? "Submitting…" : "Submit application"} <ArrowUpRight size={14} />
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

function F({ label, t, v, on, type = "text", req, placeholder }) {
  return (
    <div>
      <label className="overline text-muted-foreground">{label}{req && <span className="text-flame"> *</span>}</label>
      <input data-testid={t} type={type} value={v} onChange={on} placeholder={placeholder} className="field-line mt-2" />
    </div>
  );
}

function ConsentOption({ testid, selected, onClick, label, text }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`w-full text-left p-6 border transition-colors duration-300 ${selected ? "border-cobalt bg-secondary" : "border-border hover:border-foreground/40"}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <span className={`w-4 h-4 rounded-full border-2 ${selected ? "border-cobalt bg-cobalt" : "border-foreground/40"}`} />
        <span className="font-display uppercase tracking-tight text-lg">{label}</span>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{text}</p>
    </button>
  );
}
